import { baseApi } from './baseApi';

export interface ShopPricing {
  bw: { baseCharge: number; perPageCharge: number };
  color: { baseCharge: number; perPageCharge: number };
}

export type PaperSize = 'A4' | 'A3' | 'A5' | 'Letter' | 'Legal' | 'B5' | 'B4';
export type PaperType = 'normal' | 'bond' | 'glossy' | 'matte';
export type Orientation = 'portrait' | 'landscape';

export const PAPER_SIZE_LABELS: Record<PaperSize, string> = {
  A4: 'A4 (210 x 297 mm)',
  A3: 'A3 (297 x 420 mm)',
  A5: 'A5 (148 x 210 mm)',
  Letter: 'Letter (8.5 x 11 in)',
  Legal: 'Legal (8.5 x 14 in)',
  B5: 'B5 (176 x 250 mm)',
  B4: 'B4 (250 x 353 mm)',
};

export const PAPER_TYPE_LABELS: Record<PaperType, string> = {
  normal: 'Normal Paper',
  bond: 'Bond Paper',
  glossy: 'Glossy Paper',
  matte: 'Matte Paper',
};

// Advanced pricing by paper size and type
export interface PaperSizeTypePricing {
  [paperSize: string]: {
    [paperType: string]: {
      bw: number;
      color: number;
    };
  };
}

export interface AdvancedShopPricing {
  baseCharge: number;
  perPagePricing: PaperSizeTypePricing;
}

export interface ShopSettings {
  id?: string;
  ownerId: string;
  shopName: string;
  pricing: ShopPricing;
  advancedPricing?: AdvancedShopPricing;
  supportedPaperSizes?: PaperSize[];
  supportedPaperTypes?: PaperType[];
  upiId?: string;
  upiVerified?: boolean;
}

export const DEFAULT_PAPER_TYPES: PaperType[] = ['normal'];

export const shopSettingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getShopSettings: builder.query<ShopSettings, void>({
      query: () => '/shop/settings',
      providesTags: ['ShopSettings'],
    }),
    getShopPricing: builder.query<ShopPricing, string>({
      query: (ownerId) => `/shop/settings/pricing/${ownerId}`,
    }),
    getShopSettingsByOwner: builder.query<ShopSettings, string>({
      query: (ownerId) => `/shop/settings/public/${ownerId}`,
    }),
    updateShopSettings: builder.mutation<ShopSettings, Partial<ShopSettings>>({
      query: (body) => ({
        url: '/shop/settings',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['ShopSettings'],
    }),
    updateUpiId: builder.mutation<ShopSettings, { upiId: string }>({
      query: (body) => ({
        url: '/shop/settings/upi',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['ShopSettings'],
    }),
  }),
});

export const {
  useGetShopSettingsQuery,
  useGetShopPricingQuery,
  useGetShopSettingsByOwnerQuery,
  useUpdateShopSettingsMutation,
  useUpdateUpiIdMutation,
} = shopSettingsApi;
